/**
 * TRH Roman Shade Calculator - Browser Module
 * =========================================================================
 * Manages the Roman shade calculator form, price calculation, validation,
 * and add-to-cart flow via Shopify's standard /cart/add.js endpoint.
 *
 * Parameterized by shade style (Flat, Relaxed, Soft Fold, Tie) — style
 * constants are passed in via init().
 *
 * Uses the $1-variant trick: the shade variant costs $1, and quantity is
 * set to the calculated price in dollars. Fabric and trim are added as
 * separate line items (same pattern as TRHDraperyCalculator).
 *
 * Dependencies:
 *   - window.TRHFabricBrowser (shared fabric/trim selection module)
 *
 * Exposes:
 *   - window.TRHRomanShadeCalculator.init(config)
 *
 * @module TRHRomanShadeCalculator
 * @version 1.0.0
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Constants — set via init() per shade style
  // ---------------------------------------------------------------------------

  /** @type {number} Price per square foot for this shade style. */
  var STYLE_PRICE_PER_SQFT = 0;

  /** @type {number} Minimum order price for this shade style. */
  var STYLE_MINIMUM = 0;

  /** @type {number} Fullness multiplier for fabric length calculation. */
  var STYLE_FULLNESS = 1;

  /** @type {string} Display name for this shade style. */
  var STYLE_NAME = 'Roman Shade';

  /** @const {number} Trim labor cost per linear inch. */
  var TRIM_LABOR_PER_INCH = 1.5;

  /** @const {number} Inches added to fabric length for hems. */
  var LENGTH_ALLOWANCE = 10;

  /** @const {number} Inches added to width for seam allowance. */
  var SEAM_ALLOWANCE = 4;

  /** @const {number} Cordless upgrade cost per shade. */
  var CORDLESS_COST = 160;

  // ---------------------------------------------------------------------------
  // Module state
  // ---------------------------------------------------------------------------

  /** @type {Object|null} Currently selected fabric from TRHFabricBrowser. */
  var selectedFabric = null;

  /** @type {Object|null} Currently selected trim from TRHFabricBrowser. */
  var selectedTrim = null;

  /** @type {{fabric: number, trim: number}} Last calculated yardage. */
  var lastCalculatedYards = { fabric: 0, trim: 0 };

  /** @type {number} Last calculated total price. */
  var lastCalculatedPrice = 0;

  /** @type {Object} Last calculated price breakdown. */
  var lastCalculatedBreakdown = {
    base: 0,
    liner: 0,
    cordless: 0,
    trim: 0,
    fabric: 0,
    trimProduct: 0
  };

  /** @type {boolean} Whether a calculation has been performed. */
  var hasCalculated = false;

  /** @type {number|null} Numeric Shopify variant ID for the shade product ($1 variant). */
  var shadeVariantId = null;

  // ---------------------------------------------------------------------------
  // DOM helpers
  // ---------------------------------------------------------------------------

  function $(id) {
    return document.getElementById(id);
  }

  function hideError(id) {
    var el = $(id);
    if (el) el.style.display = 'none';
  }

  function showError(id) {
    var el = $(id);
    if (el) el.style.display = 'block';
  }

  function sanitize(str) {
    if (!str) return 'None';
    return String(str).replace(/[\r\n\t]+/g, ' ').replace(/"/g, "'").trim().slice(0, 500);
  }

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  function validateForm(showErrors) {
    var ok = true;

    var name = $('shadeName') ? $('shadeName').value.trim() : '';
    var w = $('shadeWidth') ? parseFloat($('shadeWidth').value) : NaN;
    var l = $('shadeLength') ? parseFloat($('shadeLength').value) : NaN;
    var fw = $('shadeFabricWidth') ? parseFloat($('shadeFabricWidth').value) : NaN;
    var q = $('shadeQuantity') ? parseInt($('shadeQuantity').value, 10) : NaN;
    var ownF = $('ownFabric') ? $('ownFabric').checked : false;
    var custF = $('customFabricName') ? $('customFabricName').value.trim() : '';
    var trim = $('shadeTrimPlacement') ? $('shadeTrimPlacement').value : 'none';
    var ownT = $('ownTrim') ? $('ownTrim').checked : false;
    var custT = $('customTrimName') ? $('customTrimName').value.trim() : '';

    if (!name) {
      if (showErrors) showError('shadeNameError');
      ok = false;
    }
    if (isNaN(w) || w < 1) {
      if (showErrors) showError('widthError');
      ok = false;
    }
    if (isNaN(l) || l < 1) {
      if (showErrors) showError('lengthError');
      ok = false;
    }
    if (!ownF && !selectedFabric) {
      if (showErrors) showError('fabricError');
      ok = false;
    }
    if (ownF && !custF) {
      if (showErrors) showError('customFabricNameError');
      ok = false;
    }
    if (trim !== 'none' && !ownT && !selectedTrim) {
      if (showErrors) showError('trimError');
      ok = false;
    }
    if (ownT && trim !== 'none' && !custT) {
      if (showErrors) showError('customTrimNameError');
      ok = false;
    }
    if (isNaN(fw) || fw < 36) {
      if (showErrors) showError('fabricWidthError');
      ok = false;
    }
    if (isNaN(q) || q < 1) {
      if (showErrors) showError('quantityError');
      ok = false;
    }

    return ok;
  }

  // ---------------------------------------------------------------------------
  // Add-to-cart button state
  // ---------------------------------------------------------------------------

  function updateAddToCartButton() {
    var addBtn = $('addToCartButton');
    if (!addBtn) return;
    addBtn.disabled = !hasCalculated || lastCalculatedPrice <= 0;
  }

  // ---------------------------------------------------------------------------
  // Price calculation
  // ---------------------------------------------------------------------------

  function calculateShade() {
    // Clear all errors first
    var errorIds = [
      'shadeNameError', 'widthError', 'lengthError', 'fabricError',
      'customFabricNameError', 'trimError', 'customTrimNameError',
      'fabricWidthError', 'quantityError', 'calculateError'
    ];
    errorIds.forEach(function (id) { hideError(id); });

    if (!validateForm(true)) {
      showError('calculateError');
      return;
    }
    hideError('calculateError');

    var width = parseFloat($('shadeWidth').value);
    var length = parseFloat($('shadeLength').value);
    var fabricWidth = parseFloat($('shadeFabricWidth').value);
    var quantity = parseInt($('shadeQuantity').value, 10);
    var cordOption = $('shadeCordOption').value;
    var linerOption = parseFloat($('shadeLinerOption').value);
    var trimPlacement = $('shadeTrimPlacement').value;
    var ownFabric = $('ownFabric').checked;
    var ownTrim = $('ownTrim').checked;

    // Base pricing: price per sqft
    var sqft = (width * length) / 144;
    var totalSqft = sqft * quantity;
    var basePrice = STYLE_PRICE_PER_SQFT * totalSqft;
    var linerCost = linerOption * totalSqft;
    var cordlessCost = (cordOption === 'cordless') ? CORDLESS_COST * quantity : 0;

    // Fabric yardage: (length * fullness + allowance) / 36 * widths needed
    var widthsNeeded = Math.ceil((width + SEAM_ALLOWANCE) / fabricWidth);
    var lengthWithHems = (length * STYLE_FULLNESS) + LENGTH_ALLOWANCE;
    var fabricYardsPerShade = widthsNeeded * lengthWithHems / 36;
    var totalFabricYards = Math.ceil(fabricYardsPerShade * quantity);

    // Trim calculations
    var trimPricingInches = 0;
    var trimYardageInches = 0;

    if (trimPlacement === 'center') {
      trimPricingInches = length * 1.5;
      trimYardageInches = length + 2;
    } else if (trimPlacement === 'bottom') {
      trimPricingInches = width * 1.5;
      trimYardageInches = width + 2;
    } else if (trimPlacement === 'sides') {
      trimPricingInches = length * 2 * 1.5;
      trimYardageInches = (length + 2) * 2;
    } else if (trimPlacement === 'picture') {
      trimPricingInches = (length * 2 + width) * 1.5;
      trimYardageInches = (length + 2) * 2 + (width + 2);
    }

    trimPricingInches *= quantity;
    trimYardageInches *= quantity;
    var trimYards = Math.ceil(trimYardageInches / 36);
    var trimLaborCost = (trimPlacement !== 'none') ? trimPricingInches * TRIM_LABOR_PER_INCH : 0;

    // Product costs (fabric/trim materials)
    var fabricPrice = ownFabric ? 0 : (selectedFabric ? selectedFabric.price * totalFabricYards : 0);
    var trimPrice = (ownTrim || trimPlacement === 'none') ? 0 : (selectedTrim ? selectedTrim.price * trimYards : 0);

    var total = basePrice + linerCost + cordlessCost + trimLaborCost + fabricPrice + trimPrice;
    total = Math.max(total, STYLE_MINIMUM);
    total = Math.ceil(total);

    // Save state
    lastCalculatedYards = { fabric: totalFabricYards, trim: trimYards };
    lastCalculatedPrice = total;
    lastCalculatedBreakdown = {
      base: Math.ceil(basePrice),
      liner: Math.ceil(linerCost),
      cordless: cordlessCost,
      trim: Math.ceil(trimLaborCost),
      fabric: Math.ceil(fabricPrice),
      trimProduct: Math.ceil(trimPrice)
    };
    hasCalculated = true;

    // Display results
    var laborTotal = Math.ceil(basePrice + linerCost + cordlessCost + trimLaborCost);
    var fabricDisplay = ownFabric ? 'Customer Fabric' : (selectedFabric ? '$' + Math.ceil(fabricPrice) : '$0');
    var trimDisplay = (trimPlacement === 'none') ? 'N/A' : (ownTrim ? 'Customer Trim' : (selectedTrim ? '$' + Math.ceil(trimPrice) : '$0'));

    var resultHtml = '<p style="font-size:18px;font-weight:bold;margin-bottom:10px;color:#28a745;">Total: $' + total + '</p>' +
      '<div style="font-size:14px;line-height:1.8;border-top:1px solid #ddd;padding-top:10px;">' +
      '<p><strong>Labor:</strong> $' + laborTotal + '</p>' +
      '<p><strong>Fabric:</strong> ' + totalFabricYards + ' yards - ' + fabricDisplay + '</p>';

    if (trimPlacement !== 'none') {
      resultHtml += '<p><strong>Trim:</strong> ' + trimYards + ' yards - ' + trimDisplay + '</p>';
    }
    resultHtml += '</div>';

    $('shadeResult').innerHTML = resultHtml;
    updateAddToCartButton();
  }

  // ---------------------------------------------------------------------------
  // Add to Cart via /cart/add.js
  // ---------------------------------------------------------------------------

  function buildOrderProperties() {
    var shadeName = sanitize($('shadeName').value) || 'Not specified';
    var ownFabricChecked = $('ownFabric').checked;
    var ownTrimChecked = $('ownTrim').checked;
    var trimPlacement = $('shadeTrimPlacement').value;

    var fabricDetails = '';
    if (ownFabricChecked) {
      var comFabricColor = $('customFabricColor') ? sanitize($('customFabricColor').value) : '';
      fabricDetails = sanitize($('customFabricName').value) + (comFabricColor ? ' - ' + comFabricColor : '') + ' (Customer Fabric)';
    } else if (selectedFabric) {
      var fabricYards = lastCalculatedYards.fabric;
      var fabricTotal = Math.ceil(fabricYards * selectedFabric.price);
      fabricDetails = selectedFabric.title + ' (' + fabricYards + ' yards @ $' + selectedFabric.price.toFixed(2) + '/yd = $' + fabricTotal + ')';
    } else {
      fabricDetails = 'None';
    }

    var trimDetails = '';
    if (trimPlacement === 'none') {
      trimDetails = 'None';
    } else if (ownTrimChecked) {
      var comTrimColor = $('customTrimColor') ? sanitize($('customTrimColor').value) : '';
      trimDetails = sanitize($('customTrimName').value) + (comTrimColor ? ' - ' + comTrimColor : '') + ' (Customer Trim)';
    } else if (selectedTrim) {
      var trimYardsVal = lastCalculatedYards.trim;
      var trimTotal = Math.ceil(trimYardsVal * selectedTrim.price);
      trimDetails = selectedTrim.title + ' (' + trimYardsVal + ' yards @ $' + selectedTrim.price.toFixed(2) + '/yd = $' + trimTotal + ')';
    } else {
      trimDetails = 'Selection Required';
    }

    return {
      'Shade Name': shadeName,
      'Total Price': '$' + lastCalculatedPrice,
      'Style': STYLE_NAME,
      'Width': $('shadeWidth').value + ' inches',
      'Length': $('shadeLength').value + ' inches',
      'Fabric': fabricDetails,
      'Trim Placement': trimPlacement === 'none' ? 'None' : trimPlacement,
      'Trim': trimDetails,
      'Cord Option': $('shadeCordOption').options[$('shadeCordOption').selectedIndex].text,
      'Mount Option': $('shadeMountOption').options[$('shadeMountOption').selectedIndex].text,
      'Liner': $('shadeLinerOption').options[$('shadeLinerOption').selectedIndex].text,
      'Fabric Width': $('shadeFabricWidth').value + ' inches',
      'Additional Notes': sanitize($('shadeAdditionalNotes').value) || 'None'
    };
  }

  function buildCartItems() {
    var groupId = 'shd-' + Date.now() + '-' + Math.random().toString(36).substr(2, 5);
    var shadeName = sanitize($('shadeName').value) || 'Not specified';
    var qty = parseInt($('shadeQuantity').value, 10);
    var ownFabricChecked = $('ownFabric').checked;
    var ownTrimChecked = $('ownTrim').checked;
    var trimPlacement = $('shadeTrimPlacement').value;

    // Labor cost = total minus material costs
    var fabricMaterialCost = lastCalculatedBreakdown.fabric || 0;
    var trimMaterialCost = lastCalculatedBreakdown.trimProduct || 0;
    var laborCost = Math.max(Math.round(lastCalculatedPrice - fabricMaterialCost - trimMaterialCost), 1);

    var properties = buildOrderProperties();
    properties['_group_id'] = groupId;
    properties['_shade_labor'] = 'true';
    properties['_shade_count'] = String(qty);

    var items = [];

    // 1. Shade labor — $1 variant, quantity = labor cost in dollars
    items.push({
      id: shadeVariantId,
      quantity: laborCost,
      properties: properties
    });

    // 2. Fabric (separate line item)
    if (!ownFabricChecked && selectedFabric && selectedFabric.id) {
      items.push({
        id: parseInt(selectedFabric.id, 10),
        quantity: lastCalculatedYards.fabric,
        properties: {
          '_group_id': groupId,
          '_is_sub_item': 'true',
          'For': STYLE_NAME + ' - ' + shadeName,
          'Yardage': lastCalculatedYards.fabric + ' yards'
        }
      });
    }

    // 3. Trim (separate line item)
    if (!ownTrimChecked && selectedTrim && selectedTrim.id && trimPlacement !== 'none') {
      items.push({
        id: parseInt(selectedTrim.id, 10),
        quantity: lastCalculatedYards.trim,
        properties: {
          '_group_id': groupId,
          '_is_sub_item': 'true',
          'For': STYLE_NAME + ' - ' + shadeName,
          'Trim Placement': trimPlacement
        }
      });
    }

    return items;
  }

  // ---------------------------------------------------------------------------
  // Cart messaging
  // ---------------------------------------------------------------------------

  function showCartMessage(message, isError) {
    var el = $('item_added_message');
    var msgSpan = $('cartMessage');
    var loadingBar = $('loadingBar');
    var viewBtn = $('viewCartButton');
    var icon = $('success-icon');

    if (loadingBar) loadingBar.style.display = 'none';
    if (msgSpan) {
      msgSpan.style.display = 'block';
      msgSpan.innerHTML = message;
    }
    if (el) {
      el.classList.toggle('error', isError);
      el.style.display = 'flex';
    }
    if (viewBtn) viewBtn.style.display = isError ? 'none' : 'block';
    if (icon) icon.style.display = isError ? 'none' : 'inline';
  }

  function showLoading() {
    var loadingBar = $('loadingBar');
    var bar = loadingBar ? loadingBar.querySelector('.loading-bar') : null;
    var msgSpan = $('cartMessage');

    if (bar) {
      bar.style.width = '0';
      bar.classList.remove('animate');
    }
    if (loadingBar) loadingBar.style.display = 'block';
    if (msgSpan) msgSpan.style.display = 'none';
    if (loadingBar) {
      var el = $('item_added_message');
      if (el) {
        el.classList.remove('error');
        el.style.display = 'flex';
      }
    }
    if (bar) setTimeout(function () { bar.classList.add('animate'); }, 10);
  }

  function hideLoading() {
    var loadingBar = $('loadingBar');
    var bar = loadingBar ? loadingBar.querySelector('.loading-bar') : null;
    if (bar) bar.classList.remove('animate');
    if (loadingBar) loadingBar.style.display = 'none';
  }

  // ---------------------------------------------------------------------------
  // Add to cart handler
  // ---------------------------------------------------------------------------

  function handleAddToCart() {
    if (!validateForm(true)) {
      showError('calculateError');
      return;
    }
    hideError('calculateError');

    var qty = parseInt($('shadeQuantity').value, 10);
    if (qty < 1) {
      showCartMessage('Quantity must be at least 1.', true);
      return;
    }

    if (!hasCalculated || lastCalculatedPrice <= 0) {
      showCartMessage('Please calculate the price first.', true);
      return;
    }

    if (!shadeVariantId) {
      showCartMessage('Configuration error: shade variant not found.', true);
      return;
    }

    var addBtn = $('addToCartButton');
    if (addBtn) {
      addBtn.disabled = true;
      addBtn.textContent = 'Adding to Cart...';
    }

    showLoading();

    var items = buildCartItems();

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (resp) {
        if (!resp.ok) {
          return resp.json().then(function (data) {
            throw new Error(data.description || data.message || 'Failed to add to cart');
          });
        }
        return resp.json();
      })
      .then(function () {
        hideLoading();
        showCartMessage('Successfully added to cart!', false);
        if (addBtn) {
          addBtn.disabled = false;
          addBtn.textContent = 'Add to Cart';
        }
      })
      .catch(function (err) {
        hideLoading();
        showCartMessage('<strong>Error adding to cart</strong><br>' + err.message, true);
        if (addBtn) {
          addBtn.disabled = false;
          addBtn.textContent = 'Add to Cart';
        }
      });
  }

  // ---------------------------------------------------------------------------
  // UI state management
  // ---------------------------------------------------------------------------

  function updateTrimFieldStates(trimPlacement) {
    var trimField = $('singleTrimField');
    var browseBtn = $('browseTrimsBtn');
    var ownTrimCheckbox = $('ownTrim');
    var isNone = trimPlacement === 'none';
    var isOwnTrim = ownTrimCheckbox ? ownTrimCheckbox.checked : false;

    if (trimField) trimField.disabled = isNone || isOwnTrim;
    if (browseBtn) browseBtn.disabled = isNone || isOwnTrim;
    if (ownTrimCheckbox) ownTrimCheckbox.disabled = isNone;

    if (isNone) {
      selectedTrim = null;
      if (trimField) {
        trimField.value = '';
        trimField.classList.remove('selected');
      }
      var trimSuggestions = $('trimSearchSuggestions');
      if (trimSuggestions) trimSuggestions.style.display = 'none';
    }
  }

  function handleOwnFabricChange() {
    var ownFabricCheckbox = $('ownFabric');
    var own = ownFabricCheckbox ? ownFabricCheckbox.checked : false;
    var fabricField = $('singleFabricField');
    var browseBtn = $('browseFabricsBtn');
    var customFabricLabel = $('customFabricName') ? $('customFabricName').parentElement : null;
    var swatchPreview = $('fabricSwatchPreview');
    var fabricWidthField = $('shadeFabricWidth');
    var customFabricColorLabel = $('customFabricColor') ? $('customFabricColor').parentElement : null;

    if (customFabricLabel) customFabricLabel.style.display = own ? 'block' : 'none';
    if (customFabricColorLabel) customFabricColorLabel.style.display = own ? 'block' : 'none';
    if (fabricField) {
      fabricField.disabled = own;
      fabricField.value = own
        ? ''
        : (selectedFabric ? selectedFabric.title + ' ($' + selectedFabric.price.toFixed(2) + '/yd)' : '');
      if (own) {
        fabricField.classList.remove('selected');
      } else {
        fabricField.classList.toggle('selected', !!selectedFabric);
      }
    }
    if (browseBtn) browseBtn.disabled = own;
    if (swatchPreview) swatchPreview.style.display = own ? 'none' : (selectedFabric ? 'flex' : 'none');

    if (fabricWidthField) {
      if (own) {
        selectedFabric = null;
        fabricWidthField.removeAttribute('readonly');
        fabricWidthField.style.background = '#fff';
      } else {
        fabricWidthField.setAttribute('readonly', 'readonly');
        fabricWidthField.style.background = '#f0f0f0';
        if (!selectedFabric) fabricWidthField.value = 54;
      }
    }

    updateAddToCartButton();
  }

  function handleOwnTrimChange() {
    var ownTrimCheckbox = $('ownTrim');
    var own = ownTrimCheckbox ? ownTrimCheckbox.checked : false;
    var trimField = $('singleTrimField');
    var browseBtn = $('browseTrimsBtn');
    var customTrimLabel = $('customTrimName') ? $('customTrimName').parentElement : null;
    var customTrimColorLabel = $('customTrimColor') ? $('customTrimColor').parentElement : null;
    var swatchPreview = $('trimSwatchPreview');

    if (customTrimLabel) customTrimLabel.style.display = own ? 'block' : 'none';
    if (customTrimColorLabel) customTrimColorLabel.style.display = own ? 'block' : 'none';
    if (trimField) {
      trimField.disabled = own;
      trimField.value = own
        ? ''
        : (selectedTrim ? selectedTrim.title + ' ($' + selectedTrim.price.toFixed(2) + '/yd)' : '');
      if (own) {
        trimField.classList.remove('selected');
      } else {
        trimField.classList.toggle('selected', !!selectedTrim);
      }
    }
    if (browseBtn) browseBtn.disabled = own;
    if (swatchPreview) swatchPreview.style.display = own ? 'none' : (selectedTrim ? 'flex' : 'none');

    if (own) selectedTrim = null;
    updateAddToCartButton();
  }

  // ---------------------------------------------------------------------------
  // TRHFabricBrowser integration (custom events)
  // ---------------------------------------------------------------------------

  function onFabricSelected(event) {
    var fabricData = event.detail && event.detail.fabric ? event.detail.fabric : event.detail;
    if (!fabricData) return;

    selectedFabric = {
      id: fabricData.variantId || fabricData.id || null,
      title: fabricData.title || '',
      price: fabricData.price || 0,
      imageUrl: fabricData.imageUrl || null,
      altText: fabricData.altText || fabricData.title || '',
      fabricWidth: fabricData.fabricWidth || 54
    };

    var fabricWidthField = $('shadeFabricWidth');
    if (fabricWidthField) fabricWidthField.value = selectedFabric.fabricWidth;

    if (hasCalculated) calculateShade();

    hideError('fabricError');
    updateAddToCartButton();
  }

  function onTrimSelected(event) {
    var trimData = event.detail && event.detail.trim ? event.detail.trim : event.detail;
    if (!trimData) return;

    selectedTrim = {
      id: trimData.variantId || trimData.id || null,
      title: trimData.title || '',
      price: trimData.price || 0,
      imageUrl: trimData.imageUrl || null,
      altText: trimData.altText || trimData.title || ''
    };

    if (hasCalculated) calculateShade();

    hideError('trimError');
    updateAddToCartButton();
  }

  function onFabricCleared() {
    selectedFabric = null;
    var fabricWidthField = $('shadeFabricWidth');
    if (fabricWidthField) fabricWidthField.value = 54;
    updateAddToCartButton();
  }

  function onTrimCleared() {
    selectedTrim = null;
    updateAddToCartButton();
  }

  // ---------------------------------------------------------------------------
  // Event binding
  // ---------------------------------------------------------------------------

  function bindEvents() {
    var calcBtn = $('calculateButton');
    if (calcBtn) calcBtn.addEventListener('click', calculateShade);

    var resetBtn = $('resetButton');
    if (resetBtn) resetBtn.addEventListener('click', function () { location.reload(); });

    var addBtn = $('addToCartButton');
    if (addBtn) addBtn.addEventListener('click', handleAddToCart);

    // Trim placement change
    var trimSelect = $('shadeTrimPlacement');
    if (trimSelect) {
      trimSelect.addEventListener('change', function () {
        updateTrimFieldStates(this.value);
        updateAddToCartButton();
      });
    }

    // Own fabric checkbox
    var ownFabricCheckbox = $('ownFabric');
    if (ownFabricCheckbox) ownFabricCheckbox.addEventListener('change', handleOwnFabricChange);

    // Own trim checkbox
    var ownTrimCheckbox = $('ownTrim');
    if (ownTrimCheckbox) ownTrimCheckbox.addEventListener('change', handleOwnTrimChange);

    // Form field changes — update add-to-cart state
    var formFields = [
      'shadeName', 'shadeWidth', 'shadeLength', 'shadeFabricWidth',
      'shadeQuantity', 'shadeCordOption', 'shadeMountOption', 'shadeLinerOption',
      'customFabricName', 'customTrimName'
    ];
    formFields.forEach(function (fieldId) {
      var field = $(fieldId);
      if (field) {
        field.addEventListener('input', updateAddToCartButton);
        field.addEventListener('change', updateAddToCartButton);
      }
    });

    // Listen for TRHFabricBrowser custom events
    document.addEventListener('trh:fabric-selected', onFabricSelected);
    document.addEventListener('trh:trim-selected', onTrimSelected);
    document.addEventListener('trh:fabric-cleared', onFabricCleared);
    document.addEventListener('trh:trim-cleared', onTrimCleared);
  }

  // ---------------------------------------------------------------------------
  // Initialisation
  // ---------------------------------------------------------------------------

  function setInitialState() {
    var trimValue = $('shadeTrimPlacement') ? $('shadeTrimPlacement').value : 'none';
    updateTrimFieldStates(trimValue);
    updateAddToCartButton();
  }

  /**
   * Initialise the Roman Shade Calculator module.
   *
   * @param {Object} config
   * @param {number} config.shadeVariantId    - Numeric Shopify variant ID for the $1 shade variant.
   * @param {number} config.pricePerSqft      - Price per square foot for this shade style.
   * @param {number} config.minimum           - Minimum order price.
   * @param {number} config.fullness          - Fullness multiplier for fabric length.
   * @param {string} config.styleName         - Display name (e.g. "Relaxed Roman Shade").
   */
  function init(config) {
    config = config || {};

    if (config.shadeVariantId) shadeVariantId = config.shadeVariantId;
    if (config.pricePerSqft) STYLE_PRICE_PER_SQFT = config.pricePerSqft;
    if (config.minimum) STYLE_MINIMUM = config.minimum;
    if (config.fullness) STYLE_FULLNESS = config.fullness;
    if (config.styleName) STYLE_NAME = config.styleName;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        bindEvents();
        setInitialState();
      });
    } else {
      bindEvents();
      setInitialState();
    }
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  window.TRHRomanShadeCalculator = {
    init: init,
    calculate: calculateShade,
    validate: validateForm
  };

})();
